import { DateTime } from "luxon"
import { CalendarApi, EventApi, EventDropArg, EventMountArg } from "@fullcalendar/core"
import { EventResizeDoneArg } from "@fullcalendar/interaction"

import { DateTimeFactory } from "../../../../../../modules/localization/useDateTime"
import { tailwindPalette } from "../../../../../../modules/theme/tailwindPalette"
import { ResourceScheduleItem } from "../../../createOrderTypes"

import type { TimelineCalendarEvent, TimelineCalendarResource } from "./fullCalendarTypes"

export const BUFFER_SIZE_ATTR = "--buffer-size"
export const SLOT_SIZE = 26
// keeps the seven week days readable on a phone; the week scrolls sideways instead
export const WEEK_SLOT_MIN_WIDTH_MOBILE = 96
// FullCalendar waits 1000 ms by default before a touch press may select or drag
export const TOUCH_LONG_PRESS_DELAY_MS = 300
export const SLOT_DURATION = 15

const FREE_SLOT_EVENT_COLOR = tailwindPalette.blue[300]

// dateTimeFactory's output without Luxon 3.3's slow da-DK path
const formatDigitsOnly = (dateTime: DateTime, format: string) => dateTime.setLocale("en-US").toFormat(format)
export const toIsoMinutesFast = (dateTime: DateTime) => formatDigitsOnly(dateTime, "yyyy-MM-dd'T'HH:mm")
export const toHourMinuteFast = (dateTime: DateTime) => formatDigitsOnly(dateTime, "HH:mm")

const isShortDuration = (minutes: number) => Number.isFinite(minutes) && minutes > 0 && minutes < SLOT_DURATION

function isShortDayGridEvent(
    dateTimeFactory: DateTimeFactory,
    isDayGrid: boolean,
    start: Date | null | undefined,
    end: Date | null | undefined,
): boolean {
    if (!isDayGrid) return false
    if (!start || !end) return false

    const startDt = dateTimeFactory.fromJSDate(start)
    const endDt = dateTimeFactory.fromJSDate(end)

    return isShortDuration(endDt.diff(startDt, "minutes").minutes)
}

// server events carry durationMinutes; the local draft and a drag/resize mirror need the date math
export function isShortTimelineEvent(
    dateTimeFactory: DateTimeFactory,
    isDayGrid: boolean,
    event: Pick<EventApi, "start" | "end" | "extendedProps">,
    isMirror: boolean,
): boolean {
    if (!isDayGrid) return false

    const durationMinutes = isMirror ? undefined : event.extendedProps["durationMinutes"]
    return typeof durationMinutes === "number"
        ? isShortDuration(durationMinutes)
        : isShortDayGridEvent(dateTimeFactory, isDayGrid, event.start, event.end)
}

export function toFloatingIsoMinutesFromJsDate(dateTimeFactory: DateTimeFactory, date: Date): string {
    return dateTimeFactory.toApiISOMinutesFormat(dateTimeFactory.fromJSDate(date))
}

export function isAllDayAvailabilitySlot(_dateTimeFactory: DateTimeFactory, start: DateTime, end: DateTime): boolean {
    if (!start.isValid || !end.isValid) return false
    if (!(end > start)) return false
    if (toHourMinuteFast(start) !== "00:00" || toHourMinuteFast(end) !== "00:00") return false

    const hours = end.diff(start, "hours").hours
    return hours >= 23 && hours <= 25
}

export function isSlotFullyInPast(
    dateTimeFactory: DateTimeFactory,
    end: Date | null | undefined,
    now: DateTime,
): boolean {
    if (!end) return false
    if (!now.isValid) return false
    return dateTimeFactory.fromJSDate(end).toMillis() <= now.toMillis()
}

export function withEventSignature(event: TimelineCalendarEvent): TimelineCalendarEvent {
    const signature = [
        event.start,
        event.end,
        event.resourceIds.join(","),
        event.subject,
        event.host,
        event.canEdit,
        event.outOfService,
        event.backgroundColor,
        event.isAllDayAvailabilitySlot,
    ].join("|")

    return { ...event, signature }
}

export function generateCalendarEvents(
    resources: ResourceScheduleItem[],
    intervalMinutes: number,
    dateTimeFactory: DateTimeFactory,
    calendarResourceByUid: ReadonlyMap<string, TimelineCalendarResource>,
    minDurationMinutes: number,
): TimelineCalendarEvent[] {
    const events: TimelineCalendarEvent[] = []
    const intervalMs = intervalMinutes * 60_000
    const minDurationMs = minDurationMinutes * 60_000

    for (const { resource, availabilities } of resources) {
        const calendarResource = calendarResourceByUid.get(resource.uid)
        const calendarResourceId = calendarResource?.id ?? resource.uid
        const bufferMs = (calendarResource?.buffer ?? 0) * 60_000

        for (const { startTime, availabilityView } of availabilities) {
            const viewStart = dateTimeFactory.fromISO(startTime)
            if (!viewStart.isValid) continue

            const viewStartMs = viewStart.toMillis()
            let freeRunStartIndex: number | null = null

            // index === length closes a trailing free run
            for (let index = 0; index <= availabilityView.length; index++) {
                const isFree = availabilityView[index] === "0"
                if (isFree && freeRunStartIndex === null) freeRunStartIndex = index
                if (isFree || freeRunStartIndex === null) continue

                const startMs = viewStartMs + freeRunStartIndex * intervalMs
                // freeBusy counts existing bookings' buffer, not the new booking's own one before the next
                const endMs = viewStartMs + index * intervalMs - (index < availabilityView.length ? bufferMs : 0)
                freeRunStartIndex = null
                // the confirm dialog rejects a booking of exactly the minimum
                if (endMs - startMs <= minDurationMs) continue

                const start = dateTimeFactory.fromMillis(startMs)
                const end = dateTimeFactory.fromMillis(endMs)

                events.push(
                    withEventSignature({
                        id: `free-${resource.uid}-${start.toMillis()}-${index}`,
                        resourceIds: [calendarResourceId],
                        start: toIsoMinutesFast(start),
                        end: toIsoMinutesFast(end),
                        isAllDayAvailabilitySlot: isAllDayAvailabilitySlot(dateTimeFactory, start, end),
                        backgroundColor: FREE_SLOT_EVENT_COLOR,
                    }),
                )
            }
        }
    }

    return events
}

export const getRoundedScrollTime = (
    dateTimeFactory: DateTimeFactory,
): { days: number; hours: number; minutes: number } => {
    const now = dateTimeFactory.now()
    const minusOneHour = now.minus({ hours: 1 })
    const bounded = minusOneHour < now.startOf("day") ? now.startOf("day") : minusOneHour
    const minutes = Math.floor(bounded.minute / 15) * 15
    return { days: 1, hours: bounded.hour, minutes }
}

export const isInPast = (iso: string | undefined | null): boolean => {
    if (!iso) return false
    return DateTime.fromISO(iso).diffNow().as("seconds") < 0
}

export function forceFullRevert(info: EventDropArg | EventResizeDoneArg) {
    const { oldEvent, view } = info

    info.revert()
    view.calendar.addEvent({ ...oldEvent })
}

let cachedRootFontSizePx: number | null = null

function toRootFontSizePx(): number {
    if (cachedRootFontSizePx !== null && Number.isFinite(cachedRootFontSizePx) && cachedRootFontSizePx > 0) {
        return cachedRootFontSizePx
    }
    const fontSize = globalThis.getComputedStyle?.(document.documentElement)?.fontSize
    const parsed = fontSize ? Number.parseFloat(fontSize) : Number.NaN
    cachedRootFontSizePx = Number.isFinite(parsed) && parsed > 0 ? parsed : 16
    return cachedRootFontSizePx
}

function getResourceIdFromEvent(info: EventMountArg): string | null {
    const resources = info.event.getResources()
    if (resources.length === 1) return resources[0]?.id ?? null
    return info.el.closest("[data-resource-id]")?.getAttribute("data-resource-id") ?? null
}

/** Batched version: reads all layout first, then writes all styles to minimize layout thrashing. */
export const applyEventBufferStyleBatch = (
    infos: readonly EventMountArg[],
    resourceBufferMap: Readonly<Record<string, number>>,
) => {
    const rootFontPx = toRootFontSizePx()
    const tasks: { el: HTMLElement; bufferRem: string }[] = []

    for (const info of infos) {
        const currentResourceId = getResourceIdFromEvent(info)
        const bufferForResource = currentResourceId ? (resourceBufferMap[currentResourceId] ?? 0) : 0

        if (bufferForResource <= 0) {
            tasks.push({ el: info.el, bufferRem: "0rem" })
            continue
        }

        const eventStart = info.event.start
        const eventEnd = info.event.end
        if (!eventStart || !eventEnd) continue

        const start = DateTime.fromJSDate(eventStart)
        const end = DateTime.fromJSDate(eventEnd)
        const durationMinutes = end.diff(start, "minutes").minutes
        if (!Number.isFinite(durationMinutes) || durationMinutes <= 0) continue

        const eventWidthPx = info.el.getBoundingClientRect().width
        if (!Number.isFinite(eventWidthPx) || eventWidthPx <= 0) continue

        const pxPerMinute = eventWidthPx / durationMinutes
        const bufferWidthPx = Math.round(bufferForResource * pxPerMinute)
        const bufferWidthRem = `${bufferWidthPx / rootFontPx}rem`
        tasks.push({ el: info.el, bufferRem: bufferWidthRem })
    }

    for (const { el, bufferRem } of tasks) {
        el.style.setProperty(BUFFER_SIZE_ATTR, bufferRem)
    }
}

type HasDateRange = Pick<EventApi, "start" | "end"> | Pick<TimelineCalendarEvent, "start" | "end">
const toDateTime = (value: string | Date, dateTimeFactory: DateTimeFactory) =>
    typeof value === "string" ? dateTimeFactory.fromISO(value) : dateTimeFactory.fromJSDate(value)

export const isOngoing = (event: HasDateRange, dateTimeFactory: DateTimeFactory): boolean => {
    if (!event.start || !event.end) return false

    const now = dateTimeFactory.now()
    const start = toDateTime(event.start, dateTimeFactory)
    const end = toDateTime(event.end, dateTimeFactory)

    return start.isValid && end.isValid && start <= now && now < end
}

export const isSameResources = (a: readonly string[], b: readonly string[]) => {
    const setA = new Set(a)
    return b.every((id) => setA.has(id))
}

export async function scrollToResource(resourceId?: string): Promise<void> {
    if (!resourceId) return

    const scrolls = document.querySelectorAll<HTMLElement>(".fc-scroller.fc-scroller-liquid-absolute")
    if (!scrolls.length) return

    const selector = `[data-resource-id="${resourceId}"]`

    const tryScroll = () => {
        const target = document.querySelector<HTMLElement>(selector)
        if (!target) return false

        const firstScroller = scrolls[0]
        const scrollTop = target.offsetTop - firstScroller.clientHeight / 2.5 + target.clientHeight

        scrolls.forEach((s) => {
            s.scrollTop = scrollTop
        })

        return true
    }

    return new Promise<void>((resolve) => {
        requestAnimationFrame(() => {
            if (tryScroll()) {
                resolve()
                return
            }

            const firstScroller = scrolls[0]
            const observer = new MutationObserver(() => {
                if (tryScroll()) {
                    observer.disconnect()
                    resolve()
                }
            })

            observer.observe(firstScroller, { childList: true, subtree: true })

            setTimeout(() => {
                observer.disconnect()
                resolve()
            }, 3000)
        })
    })
}

export function applyDynamicTimeBuffer(calendarApi: CalendarApi, start: DateTime) {
    calendarApi.setOption("slotMinTime", start.toFormat("HH:mm:ss"))
    calendarApi.setOption("slotMaxTime", `${start.hour + 24}:00:00`)
    calendarApi.setOption("duration", { days: 2 })
}

export const withBatchRendering = (calendarApi: CalendarApi, callback: () => void) => {
    const api = calendarApi as CalendarApi & {
        batchRendering?: (batchCallback: () => void) => void
    }

    if (typeof api.batchRendering === "function") {
        api.batchRendering(callback)
        return
    }

    callback()
}
